import { Package, Clock, ChevronDown, ShoppingCart } from 'lucide-react';
import { CoinIcon } from '@/app/components/SvgIcons';
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/app/lib/axios";
import PaginationWrapper from "@/app/components/Wrappers/PaginationWrapper";
import {formatDate} from "@/Utils/FormatDate";

interface OrderItem {
    id: number;
    order_id: number;
    product_id: number;
    product_title: string;
    price: string;
    quantity: number;
    subtotal: string;
    created_at: string;
    updated_at: string;
}

interface Order {
    id: number;
    user_id: number;
    order_number: string;
    status: string;
    total: number;
    created_at: string;
    updated_at: string;
    items: OrderItem[];
}

interface OrdersResponse {
    data: Order[];
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
}



export function OrdersTab({}) {
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);

    // Fetch orders with TanStack Query
    const { data, isLoading, isError, error } = useQuery<OrdersResponse>({
        queryKey: ['orders', currentPage],
        queryFn: async () => {
            const response = await api.get("orders", {
                params: { page: currentPage }
            });
            return response.data;
        },
        staleTime: 30000, // 30 seconds
        refetchOnWindowFocus: false,
    });

    const handlePageChange = (page: number) => {
        if (page > 0 && data && page <= data.last_page && page !== currentPage) {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const toggleOrderExpand = (orderId: number) => {
        setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="text-center py-12 text-gray-400">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto mb-3"></div>
                <p className="text-xs text-gray-400">Loading orders…</p>
            </div>
        );
    }

    // Error state
    if (isError) {
        return (
            <div className="text-center py-12 text-red-400">
                <Package size={48} className="mx-auto mb-3 opacity-50" />
                <p className="text-xs">Failed to load orders</p>
                <p className="text-xs text-gray-400 mt-2">
                    {error instanceof Error ? error.message : 'Unknown error'}
                </p>
            </div>
        );
    }

    // Empty state
    if (!data || data.data.length === 0) {
        return (
            <div className="text-center py-12 text-gray-500">
                <Package size={48} className="mx-auto mb-3 opacity-50" />
                <p className="text-xs text-gray-400">No orders yet</p>
            </div>
        );
    }

    return (
        <div>
            {/* Summary Stats */}
            <div className="mb-6 p-4 bg-gray-800/50 rounded-xl">
                <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Total orders</span>
                    <span className="font-mono font-bold text-white tabular-nums">{data.total}</span>
                </div>
            </div>

            {/* Top Pagination */}
            <div className="mb-6">
                <PaginationWrapper
                    isLoading={isLoading}
                    currentPage={data.current_page}
                    lastPage={data.last_page}
                    onPageChange={handlePageChange}
                />
            </div>

            {/* Orders List */}
            <div className="space-y-3">
                {data.data.map(order => (
                    <OrderCard
                        key={order.id}
                        order={order}
                        isExpanded={expandedOrderId === order.id}
                        onToggle={() => toggleOrderExpand(order.id)}
                    />
                ))}
            </div>

            {/* Bottom Pagination */}
            <div className="mt-6">
                <PaginationWrapper
                    isLoading={isLoading}
                    currentPage={data.current_page}
                    lastPage={data.last_page}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
}

interface OrderCardProps {
    order: Order;
    isExpanded: boolean;
    onToggle: () => void;
}

function OrderCard({ order, isExpanded, onToggle }: OrderCardProps) {
    const totalQuantity = order.items.reduce((sum, item) => sum + item.quantity, 0);

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'completed':
                return 'bg-green-500/20 text-green-400';
            case 'pending':
                return 'bg-yellow-500/20 text-yellow-400';
            case 'cancelled':
                return 'bg-red-500/20 text-red-400';
            case 'processing':
                return 'bg-blue-500/20 text-blue-400';
            default:
                return 'bg-gray-500/20 text-gray-400';
        }
    };

    return (
        <div className="bg-gray-800/50 rounded-xl hover:bg-gray-800/70 transition-colors">
            {/* Order Header - Clickable */}
            <button
                onClick={onToggle}
                className="w-full p-4 text-left focus:outline-none focus:ring-2 focus:ring-green-500/50 rounded-xl"
            >
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div className="font-mono font-bold text-white tabular-nums">
                            #{order.order_number}
                        </div>
                        <div className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide ${getStatusColor(order.status)}`}>
                            {order.status.toUpperCase()}
                        </div>
                    </div>
                    <ChevronDown
                        className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                            isExpanded ? 'rotate-180' : ''
                        }`}
                    />
                </div>

                <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-[11px] text-gray-400">
                            <Clock size={14} />
                            {formatDate(order.created_at)}
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-gray-400">
                            <ShoppingCart size={14} />
                            {totalQuantity} {totalQuantity === 1 ? 'item' : 'items'}
                        </div>
                    </div>
                    <div className="flex items-center gap-1 text-white font-mono font-bold tabular-nums">
                        <span>{order.total.toLocaleString()}</span>
                        <CoinIcon size={16} />
                    </div>
                </div>
            </button>

            {/* Order Items - Expandable */}
            {isExpanded && (
                <div className="border-t border-white/5 p-4 bg-gray-800/30">
                    <div className="space-y-3">
                        {order.items.map((item) => (
                            <div
                                key={item.id}
                                className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg"
                            >
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-gray-200 mb-1">
                                        {item.product_title}
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        Quantity: {item.quantity} × {parseFloat(item.price).toLocaleString()} Kick Points
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 text-green-400 font-mono font-bold tabular-nums">
                                    <span>{parseFloat(item.subtotal).toLocaleString()}</span>
                                    <CoinIcon size={14} />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Order Total Summary */}
                    <div className="mt-4 pt-3 border-t border-white/5">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Order total</span>
                            <div className="flex items-center gap-1 text-white font-mono font-bold text-lg tabular-nums">
                                <span>{order.total.toLocaleString()}</span>
                                <CoinIcon size={18} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}